import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Field,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
} from '@fluentui/react-components';
import React from 'react';
import { Controller } from 'react-hook-form';
import { yesOption, noOption } from '../../../../utils/types/CommonDropdownOptions';
import CanAccess from '../../../common/canAccess/CanAccess';
import IAnnualFilingRequirementsFormProps from './IAnnualFilingRequirementsFormProps';

const FinancialSystemsForm = ({
  annualFilingRequirements,
  form,
}: IAnnualFilingRequirementsFormProps): JSX.Element => {
  const { control, errors } = form;

  const maxLengthRules = {
    maxLength: {
      value: 250,
      message: 'Input is greater than the maximum character limit of 250.',
    },
  };
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="localAccountSystem"
          control={control}
          defaultValue={annualFilingRequirements.localAccountSystem || ''}
          rules={maxLengthRules}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field
              label="Local Account System (if not SAP)"
              validationMessage={errors?.localAccountSystem?.message as string}
            >
              <Textarea
                id="localAccountSystem"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="financialRecordLocation"
          control={control}
          defaultValue={annualFilingRequirements.financialRecordLocation || ''}
          rules={maxLengthRules}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field
              label="Finance Record Location"
              validationMessage={errors?.financialRecordLocation?.message as string}
            >
              <Textarea
                id="financialRecordLocation"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="localLanguageRequirements"
          control={control}
          defaultValue={annualFilingRequirements.localLanguageRequirements || ''}
          rules={maxLengthRules}
          render={({ onChange, onBlur, value, name, ref }): React.ReactElement => (
            <Field
              label="Local Language Requirements"
              validationMessage={errors?.localLanguageRequirements?.message as string}
            >
              <Textarea
                id="localLanguageRequirements"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="calendarYearLedgerRequired"
          control={control}
          defaultValue={annualFilingRequirements.calendarYearLedgerRequired}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Is Calendar Year Ledger Required">
              <RadioGroup
                id="calendarYearLedgerRequired"
                name={name}
                value={value?.toString()}
                onChange={(e, radioData): void => {
                  onChange(radioData.value === 'true');
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
        <CanAccess requestedAction="HeadCount:Edit">
          <Controller
            name="headCount"
            control={control}
            defaultValue={annualFilingRequirements.headCount}
            render={({ onChange, value, name }): React.ReactElement => (
              <Field label="Head Count">
                <RadioGroup
                  id="headCount"
                  name={name}
                  value={value?.toString()}
                  onChange={(e, radioData): void => {
                    onChange(radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            )}
          />
        </CanAccess>
        <Controller
          name="localChartOfAccountsRequired"
          control={control}
          defaultValue={annualFilingRequirements.localChartOfAccountsRequired}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Is Local Chart Of Accounts Required">
              <RadioGroup
                id="localChartOfAccountsRequired"
                name={name}
                value={value?.toString()}
                onChange={(e, radioData): void => {
                  onChange(radioData.value === 'true');
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
        <Controller
          name="isMsMarketUser"
          control={control}
          defaultValue={annualFilingRequirements.isMsMarketUser}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Is MS Market User">
              <RadioGroup
                id="isMsMarketUser"
                name={name}
                value={value?.toString()}
                onChange={(e, radioData): void => {
                  onChange(radioData.value === 'true');
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default FinancialSystemsForm;
